import type { ImportNotesInput, ImportNotesResultDto } from '@keep/shared';
import type { ImportPlan, PlannedNote } from './keep-plan';

export interface ImportDeps {
  importBatch: (input: ImportNotesInput) => Promise<ImportNotesResultDto>;
  uploadImage: (noteId: string, file: File) => Promise<unknown>;
}

export interface ImportProgress {
  notesDone: number;
  notesTotal: number;
  imagesDone: number;
  imagesTotal: number;
}

export interface ImportReport {
  created: number;
  skipped: number;
  images: number;
  labels: number;
  failures: Array<{ source: string; message: string }>;
  /** Import arrêté avant la fin (annulation ou erreur bloquante) : le relancer reprend où il s'était arrêté. */
  interrupted: string | null;
}

export interface ImportOptions {
  includeTrashed: boolean;
  batchSize?: number;
  imageConcurrency?: number;
  signal?: AbortSignal;
}

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));

/**
 * Envoie le plan à l'API par lots, puis les images de chaque lot.
 * L'API ignorant les notes déjà importées, un import interrompu peut simplement être relancé ;
 * les images manquantes d'une note déjà importée sont alors complétées.
 */
export async function runImport(
  plan: ImportPlan,
  deps: ImportDeps,
  options: ImportOptions,
  onProgress: (progress: ImportProgress) => void,
): Promise<ImportReport> {
  const { batchSize = 25, imageConcurrency = 3, signal } = options;
  const notes = plan.notes.filter((planned) => options.includeTrashed || !planned.note.trashed);
  const report: ImportReport = {
    created: 0,
    skipped: 0,
    images: 0,
    labels: 0,
    failures: [],
    interrupted: null,
  };
  const progress: ImportProgress = {
    notesDone: 0,
    notesTotal: notes.length,
    imagesDone: 0,
    imagesTotal: notes.reduce((sum, planned) => sum + planned.attachments.length, 0),
  };
  onProgress({ ...progress });

  try {
    for (let start = 0; start === 0 || start < notes.length; start += batchSize) {
      signal?.throwIfAborted();
      const batch = notes.slice(start, start + batchSize);
      const { results, createdLabels } = await deps.importBatch({
        notes: batch.map((planned) => planned.note),
        // Les libellés sans note (Labels.txt) sont créés avec le premier lot.
        ...(start === 0 && { labels: plan.labels }),
      });
      report.labels += createdLabels;

      const uploads: Upload[] = [];
      results.forEach((result, index) => {
        const planned = batch[index]!;
        if (result.status === 'created') report.created++;
        else report.skipped++;
        // Les images d'une note partent dans l'ordre, une à une : après une interruption,
        // celles déjà présentes sont exactement les premières.
        const alreadyUploaded = Math.min(result.attachmentCount, planned.attachments.length);
        progress.imagesDone += alreadyUploaded;
        if (alreadyUploaded < planned.attachments.length) {
          uploads.push({ planned, noteId: result.noteId, from: alreadyUploaded });
        }
      });
      progress.notesDone += batch.length;
      onProgress({ ...progress });

      await uploadImages(uploads, deps, imageConcurrency, report, progress, onProgress, signal);
      if (notes.length === 0) break;
    }
  } catch (error) {
    report.interrupted = signal?.aborted ? 'Import annulé' : errorText(error);
  }
  return report;
}

interface Upload {
  planned: PlannedNote;
  noteId: string;
  /** Index de la première image à envoyer. */
  from: number;
}

/** Plusieurs notes en parallèle, mais les images d'une même note dans l'ordre. */
async function uploadImages(
  uploads: Upload[],
  deps: ImportDeps,
  concurrency: number,
  report: ImportReport,
  progress: ImportProgress,
  onProgress: (progress: ImportProgress) => void,
  signal?: AbortSignal,
) {
  const queue = [...uploads];
  const worker = async () => {
    for (let task = queue.shift(); task; task = queue.shift()) {
      const { planned, noteId, from } = task;
      const pending = planned.attachments.slice(from);
      for (const [index, attachment] of pending.entries()) {
        signal?.throwIfAborted();
        try {
          const data = await attachment.entry.read();
          const file = new File([data as BlobPart], attachment.name, {
            type: attachment.mimeType ?? '',
          });
          await deps.uploadImage(noteId, file);
          report.images++;
          progress.imagesDone++;
          onProgress({ ...progress });
        } catch (error) {
          if (signal?.aborted) throw error;
          // On s'arrête à la première erreur pour cette note : les images présentes restent
          // exactement les premières, et une relance reprendra au bon endroit.
          const skipped = pending.length - index;
          report.failures.push({
            source: planned.source,
            message:
              `${attachment.name} : ${errorText(error)}` +
              (skipped > 1 ? ` (${skipped - 1} autre(s) image(s) de la note non envoyée(s))` : ''),
          });
          progress.imagesDone += skipped;
          onProgress({ ...progress });
          break;
        }
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
}
